import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Building2, Plus, Check, Loader2 } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import api from '../services/api';

/**
 * ManufacturerAutocomplete Component
 * 
 * Smart typeahead input for manufacturer selection that:
 * - Shows existing manufacturers from database as user types
 * - Displays part count for each manufacturer
 * - Prevents duplicate entries with typos
 * - Allows creating new manufacturers
 * 
 * Design inspired by Google, Amazon, Airbnb autocomplete patterns.
 */
const ManufacturerAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "e.g., Danfoss, Siemens, Allen-Bradley",
  disabled = false,
  className = ""
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [totalManufacturers, setTotalManufacturers] = useState(0);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const isSelectingRef = useRef(false); // Track when user is selecting a suggestion

  // Sync external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Load suggestions from API
  const loadSuggestions = useCallback(async (query) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/v1/parts/manufacturers/autocomplete`, {
        params: { q: query, limit: 8 }
      });
      setSuggestions(response.data.suggestions || []);
      setTotalManufacturers(response.data.total_manufacturers || 0);
    } catch (error) {
      console.error('Failed to load manufacturer suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useDebouncedCallback(loadSuggestions, 200);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);
    
    if (newValue.trim()) {
      setIsOpen(true);
      debouncedSearch(newValue);
    } else {
      // Empty input - show top manufacturers
      setIsOpen(true);
      loadSuggestions('');
    }
  };

  // Handle suggestion selection
  const handleSelect = (manufacturer) => {
    isSelectingRef.current = true; // Mark that we're selecting
    setInputValue(manufacturer.name);
    onChange(manufacturer.name);
    setIsOpen(false);
    setHighlightedIndex(-1);
    // Reset the flag after a short delay
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  // Handle "Create new" option
  const handleCreateNew = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      isSelectingRef.current = true; // Mark that we're selecting
      onChange(trimmedValue);
      setIsOpen(false);
      // Reset the flag after a short delay
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsOpen(true);
    if (!suggestions.length) {
      loadSuggestions(inputValue || '');
    }
  };

  // Handle blur with delay for click events
  const handleBlur = () => {
    setTimeout(() => {
      // Don't process blur if user is selecting a suggestion
      if (isSelectingRef.current) {
        return;
      }
      setIsOpen(false);
      // Commit current value on blur
      if (inputValue.trim() && inputValue !== value) {
        onChange(inputValue.trim());
      }
    }, 200);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        loadSuggestions(inputValue || '');
      }
      return;
    }

    const maxIndex = suggestions.length; // +1 for "Create new" option

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, maxIndex));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        } else if (highlightedIndex === suggestions.length && inputValue.trim()) {
          handleCreateNew();
        } else if (inputValue.trim()) {
          handleCreateNew();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if current input matches any suggestion exactly
  const hasExactMatch = suggestions.some(
    s => s.name.toLowerCase() === inputValue.trim().toLowerCase()
  );

  // Check if it's a potential duplicate (similar name exists)
  const potentialDuplicates = suggestions.filter(s => {
    const inputLower = inputValue.trim().toLowerCase();
    const suggestionLower = s.name.toLowerCase();
    return inputLower !== suggestionLower && 
           (suggestionLower.includes(inputLower) || inputLower.includes(suggestionLower));
  });

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          ) : (
            <Building2 className="w-4 h-4 text-slate-400" />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent transition-all"
          data-testid="manufacturer-autocomplete-input"
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="manufacturer-listbox"
          aria-activedescendant={highlightedIndex >= 0 ? `mfr-option-${highlightedIndex}` : undefined}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          id="manufacturer-listbox"
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          data-testid="manufacturer-dropdown"
        >
          {/* Header */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                {inputValue.trim() ? 'Suggestions' : 'Popular Manufacturers'}
              </span>
              <span className="text-xs text-slate-400">
                {totalManufacturers} total
              </span>
            </div>
          </div>

          {/* Suggestions List */}
          <div className="max-h-64 overflow-y-auto">
            {suggestions.length > 0 ? (
              suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.name}
                  id={`mfr-option-${index}`}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur from firing before click
                    handleSelect(suggestion);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`w-full px-3 py-2.5 flex items-center justify-between text-left transition-colors ${
                    highlightedIndex === index
                      ? 'bg-blue-50'
                      : 'hover:bg-slate-50'
                  } ${suggestion.is_exact_match ? 'bg-green-50' : ''}`}
                  data-testid={`manufacturer-option-${suggestion.name}`}
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    {suggestion.is_exact_match && (
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                    <span className={`font-medium truncate ${
                      suggestion.is_exact_match 
                        ? 'text-green-800' 
                        : 'text-slate-800'
                    }`}>
                      {highlightMatch(suggestion.name, inputValue)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      suggestion.part_count >= 10 
                        ? 'bg-blue-100 text-blue-700'
                        : suggestion.part_count >= 5
                        ? 'bg-slate-100 text-slate-600'
                        : 'bg-slate-50 text-slate-400'
                    }`}>
                      {suggestion.part_count} {suggestion.part_count === 1 ? 'part' : 'parts'}
                    </span>
                  </div>
                </button>
              ))
            ) : isLoading ? (
              <div className="px-3 py-4 text-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                <span className="text-sm">Searching...</span>
              </div>
            ) : inputValue.trim() ? (
              <div className="px-3 py-4 text-center text-slate-400">
                <span className="text-sm">No matching manufacturers found</span>
              </div>
            ) : null}

            {/* Create New Option */}
            {inputValue.trim() && !hasExactMatch && (
              <button
                id={`mfr-option-${suggestions.length}`}
                role="option"
                aria-selected={highlightedIndex === suggestions.length}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur from firing before click
                  handleCreateNew();
                }}
                onMouseEnter={() => setHighlightedIndex(suggestions.length)}
                className={`w-full px-3 py-2.5 flex items-center space-x-3 text-left border-t border-slate-100 transition-colors ${
                  highlightedIndex === suggestions.length
                    ? 'bg-emerald-50'
                    : 'hover:bg-slate-50'
                }`}
                data-testid="manufacturer-create-new"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-emerald-700">
                    Create &ldquo;{inputValue.trim()}&rdquo;
                  </span>
                  <span className="block text-xs text-slate-400">
                    Add as new manufacturer
                  </span>
                </div>
              </button>
            )}
          </div>

          {/* Warning for potential duplicates */}
          {inputValue.trim() && !hasExactMatch && potentialDuplicates.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-100">
              <p className="text-xs text-amber-700">
                <span className="font-medium">Tip:</span> Similar names exist. 
                Please verify to avoid duplicates.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Highlight matching text in suggestion
 */
const highlightMatch = (text, query) => {
  if (!query.trim()) return text;
  
  const queryLower = query.toLowerCase();
  const textLower = text.toLowerCase();
  const index = textLower.indexOf(queryLower);
  
  if (index === -1) return text;
  
  return (
    <>
      {text.slice(0, index)}
      <span className="bg-yellow-200 text-yellow-900 font-semibold">
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
};

export default ManufacturerAutocomplete;
